import React, { useState } from 'react';
import { Phone, Mail, MapPin, Instagram, Facebook } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(t('contact_success'));
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error(t('error'));
      console.error('Error sending message:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 bg-[#0A0A0A]" data-testid="contact-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="decorative-line mx-auto mb-8" />
          <p className="text-gold text-xs tracking-[0.4em] uppercase mb-4">
            {t('contact_subtitle')}
          </p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white">
            {t('contact_title')}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div>
            <h2 className="font-heading text-3xl text-white mb-8">
              Destilerija <span className="gold-text">Kovačić</span>
            </h2>
            
            <div className="space-y-6 mb-12">
              <a
                href="tel:+381604631241"
                className="flex items-center gap-4 text-white/50 hover:text-gold transition-colors group"
              >
                <div className="w-12 h-12 border border-white/10 group-hover:border-gold/30 flex items-center justify-center transition-colors">
                  <Phone size={18} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-white/30 uppercase tracking-wider">{t('contact_phone')}</p>
                  <p className="text-white/80">+381 60 463 1241</p>
                </div>
              </a>

              <a
                href="mailto:destilerijakovacic@gmail.com"
                className="flex items-center gap-4 text-white/50 hover:text-gold transition-colors group"
              >
                <div className="w-12 h-12 border border-white/10 group-hover:border-gold/30 flex items-center justify-center transition-colors">
                  <Mail size={18} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-white/30 uppercase tracking-wider">Email</p>
                  <p className="text-white/80">destilerijakovacic@gmail.com</p>
                </div>
              </a>

              <div className="flex items-center gap-4 text-white/50">
                <div className="w-12 h-12 border border-white/10 flex items-center justify-center">
                  <MapPin size={18} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-white/30 uppercase tracking-wider">{t('contact_address')}</p>
                  <p className="text-white/80">{t('contact_location')}</p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <p className="text-xs text-white/30 uppercase tracking-wider mb-4">{t('footer_follow')}</p>
              <div className="flex gap-4">
                <a
                  href="https://www.instagram.com/destilerija.kovacic/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 border border-white/10 hover:border-gold/30 flex items-center justify-center text-white/50 hover:text-gold transition-all"
                  data-testid="contact-instagram"
                >
                  <Instagram size={18} strokeWidth={1.5} />
                </a>
                <a
                  href="https://www.facebook.com/destilerija.kovacic"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 border border-white/10 hover:border-gold/30 flex items-center justify-center text-white/50 hover:text-gold transition-all"
                  data-testid="contact-facebook"
                >
                  <Facebook size={18} strokeWidth={1.5} />
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <div className="glass p-8 elegant-border">
              <h2 className="font-heading text-2xl text-white mb-8">
                {language === 'sr' ? 'Pišite nam' : 'Write to us'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-xs text-white/30 uppercase tracking-wider mb-2">
                    {t('contact_name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b border-white/10 focus:border-gold outline-none py-3 text-white placeholder:text-white/20 transition-colors"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs text-white/30 uppercase tracking-wider mb-2">
                    {t('contact_email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b border-white/10 focus:border-gold outline-none py-3 text-white placeholder:text-white/20 transition-colors"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-xs text-white/30 uppercase tracking-wider mb-2">
                    {t('contact_message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full bg-transparent border-b border-white/10 focus:border-gold outline-none py-3 text-white placeholder:text-white/20 transition-colors resize-none"
                    data-testid="contact-message-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gold hover:bg-gold-light text-black py-4 text-sm tracking-[0.15em] uppercase font-medium transition-colors duration-300 disabled:opacity-50"
                  data-testid="contact-submit"
                >
                  {loading ? t('loading') : t('contact_send')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
